import { useId, type InputHTMLAttributes, type JSX, type ReactNode, type Ref } from 'react';

/** `type` is fixed by the component; `id` comes from `useId`. */
interface ConsentCheckboxProps extends Omit<InputHTMLAttributes<HTMLInputElement>, 'id' | 'type'> {
  /** May contain links: consent text always points at the document it refers to. */
  label: ReactNode;
  error?: string | undefined;
  ref?: Ref<HTMLInputElement> | undefined;
}

/**
 * A checkbox whose label is a sentence, for the two acknowledgements that gate
 * registration (docs/north-america-launch-requirements.md §6.4).
 *
 * Separate from `Field` because the two are not the same shape: here the control
 * comes first and the text after it, the text may contain links, and there is no
 * floating label or hint line. Accessibility is wired the same way — `htmlFor`
 * on the label, `aria-describedby` on the input, `role="alert"` on the error.
 */
export function ConsentCheckbox({
  label,
  error,
  ref,
  ...input
}: ConsentCheckboxProps): JSX.Element {
  const id = useId();
  const errorId = `${id}-error`;

  return (
    <div className="flex flex-col gap-1.5">
      <div className="flex items-start gap-2.5">
        <input
          {...input}
          id={id}
          ref={ref}
          type="checkbox"
          aria-invalid={error ? true : undefined}
          aria-describedby={error ? errorId : undefined}
          className="mt-0.5 h-4 w-4 shrink-0 rounded border-[var(--kitup-color-muted)]/60 accent-[var(--kitup-color-accent)] focus-visible:ring-2 focus-visible:ring-[var(--kitup-color-accent)]/40 aria-[invalid=true]:border-red-500"
        />
        <label htmlFor={id} className="text-sm leading-5 text-[var(--kitup-color-ink)]">
          {label}
        </label>
      </div>

      {error ? (
        <p id={errorId} role="alert" className="text-xs text-red-600">
          {error}
        </p>
      ) : null}
    </div>
  );
}
